import { normalizeFormOfEducationValue } from "@/src/constants/AdmissionOffice/Malta/maltaFormOfEducationOptions";
import "./style.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomFullNamePopUp from "@/src/components/CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import CustomDocumentPreparationPopUp from "@/src/components/CustomUtils/CustomDocumentPreparationPopUp/CustomDocumentPreparationPopUp";
import APHeader from "@/src/components/ApplicationsPage/APHeader/APHeader";
import APBody from "@/src/components/ApplicationsPage/APBody/APBody";
import APFooter from "@/src/components/ApplicationsPage/APFooter/APFooter";
import { RootState } from "@/src/store";
import { IApplyInfoData, IFiltersList, IRowData, ITableResponse } from "@/src/types/applicationOnline";
import ApplyOnline from "../ApplyOnline/ApplyOnline";
import { applicationPageRequests } from "@/src/api/applicationPageRequests";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { setData } from "@/src/store/slices/ApplicationPage/applicationsTableSlice";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { filterRowsByAccess, resolveAccessFilteredCount } from "@/src/utils/accessControl/filterByAccess";
import { resolveCountryAccessFromRole } from "@/src/utils/accessControl/resolveCountryAccessFromRole";
import { resolveApplicationStar } from "@/src/utils/applications/resolveApplicationStar";

type GroupedApplyInfoItem = Partial<IApplyInfoData>;
type GroupedRow = {
  applyInfo?: GroupedApplyInfoItem[];
  userInfo?: Partial<IRowData["userInfo"]> & { student_id?: number | string; id?: number | string; user_id?: number | string };
};

const isGroupedRow = (item: unknown): item is GroupedRow => typeof item === "object" && item !== null && "applyInfo" in item;

const resolveLevelEducation = (row: Partial<IApplyInfoData> & { level_education?: string }): string =>
  String(row.levelEducation ?? row.level_education ?? "").trim();

const resolveFormStudy = (row: Partial<IApplyInfoData> & { form_study?: string }): string =>
  normalizeFormOfEducationValue(row.formStudy ?? row.form_study);

const parsePossibleId = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseInt(value.replace(/\D+/g, ""), 10);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }
  return undefined;
};

const normalizeGroupedRows = (groupedRows: GroupedRow[]): IApplyInfoData[] =>
  groupedRows.flatMap((group) => {
    const applyRows = Array.isArray(group.applyInfo) ? group.applyInfo : [];
    const userInfo = group.userInfo ?? {};
    return applyRows
      .filter((applyRow) => applyRow.id != null)
      .map((applyRow) => {
        const applicantId =
          parsePossibleId(applyRow.applicant_id) ??
          parsePossibleId((applyRow as { student_id?: number | string }).student_id) ??
          parsePossibleId((applyRow as { studentId?: number | string }).studentId) ??
          0;
        return {
          date: applyRow.date ?? "",
          id: Number(applyRow.id),
          form_status: applyRow.form_status ?? (applyRow as { status?: string }).status ?? "",
          course: applyRow.course ?? "",
          levelEducation: resolveLevelEducation(applyRow),
          formStudy: resolveFormStudy(applyRow),
          updated: applyRow.updated ?? "",
          form_type: applyRow.form_type ?? (applyRow as { type?: string }).type ?? "",
          assignee: applyRow.assignee ?? "",
          comments: applyRow.comments ?? [],
          is_read: Boolean(applyRow.is_read) as true,
          color_star: resolveApplicationStar(applyRow.color_star ?? "default").color,
          fullname: userInfo.fullname ?? "",
          email: userInfo.email ?? "",
          phone: userInfo.phone ?? "",
          country: userInfo.country ?? "",
          nationality: userInfo.nationality ?? "",
          dateOfBirth: userInfo.dateOfBirth ?? "",
          address: userInfo.address ?? "",
          passportNo: userInfo.passportNo ?? "",
          passportIssued: userInfo.passportIssued ?? "",
          validPassport: userInfo.validPassport ?? "",
          studentStatus:
            (userInfo as { studentStatus?: string; student_status?: string }).studentStatus ??
            (userInfo as { student_status?: string }).student_status ??
            "",
          applicant_id: applicantId,
          form_office:
            String(
              (applyRow as { form_office?: string }).form_office ??
                (userInfo as { form_office?: string }).form_office ??
                "",
            ).trim() || undefined,
          office_url:
            String(
              (applyRow as { office_url?: string }).office_url ??
                (userInfo as { office_url?: string }).office_url ??
                "",
            ).trim() || undefined,
        };
      });
  });

const normalizeApplicationsResponse = (payload: unknown): { data: IApplyInfoData[]; count_form: number } => {
  if (!payload) return { data: [], count_form: 0 };

  if (typeof payload === "object" && payload !== null && "data" in payload) {
    const regularPayload = payload as { data?: unknown[]; count_form?: number };
    const rows = Array.isArray(regularPayload.data) ? regularPayload.data : [];
    const normalizedRows =
      rows.length > 0 && isGroupedRow(rows[0]) ? normalizeGroupedRows(rows as GroupedRow[]) : (rows as IApplyInfoData[]);
    return { data: normalizedRows, count_form: regularPayload.count_form ?? normalizedRows.length };
  }

  if (Array.isArray(payload)) {
    const rows = payload as unknown[];
    const normalizedRows =
      rows.length > 0 && isGroupedRow(rows[0]) ? normalizeGroupedRows(rows as GroupedRow[]) : (rows as IApplyInfoData[]);
    return { data: normalizedRows, count_form: normalizedRows.length };
  }

  return { data: [], count_form: 0 };
};

const APLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let actualOfficeTable = useSelector((state: RootState) => state.applicationsFilters.actualOfficeTable);
  const actualFilterStatus = useSelector((state: RootState) => state.applicationsFilters.filterStatus);
  const applicationsTable = useSelector((state: RootState) => state.applicationsTable);
  const selectedSiteUrl = useSelector((state: RootState) => state.accessControl.selectedSiteUrl);
  const userRole = useSelector((state: RootState) => state.accessControl.userRole);

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const [isShowSettingsModal, setIsShowSettingsModal] = useState(false);
  const [isApPopUpFullName, setIsApPopUpFullName] = useState<boolean>(false);
  const [isDocumentPreparationPopUpOpen, setIsDocumentPreparationPopUpOpen] = useState<boolean>(false);
  const [currentDataPopUp, setCurrentDataPopUp] = useState<IRowData["userInfo"] | undefined>();
  const [currentStudentId, setCurrentStudentId] = useState<number | undefined>();
  const [currentApplyInfoId, setCurrentApplyInfoId] = useState<number | undefined>();
  const [isCurrentPage, setIsCurrentPage] = useState<string>("1");
  const [isSelectPagination, setIsSelectPagination] = useState<string>("5");

  const [tableFiltersList, setTableFiltersList] = useState<IFiltersList | undefined>();

  const [actualQuantityRows, setActualQuantityRows] = useState<number>(0);
  const [isApplyOnlineOpen, setIsApplyOnlineOpen] = useState(
    () => localStorage.getItem("applyOnlineApplications") === "show",
  );

  const syncApplyOnlineOpen = useCallback(() => {
    setIsApplyOnlineOpen(localStorage.getItem("applyOnlineApplications") === "show");
  }, []);

  const closeApplyOnlineForm = useCallback(() => {
    localStorage.removeItem("applyOnlineApplications");
    window.dispatchEvent(new Event("applyOnlineApplicationsChanged"));
  }, []);

  const loadActualForm = async () => {
    dispatch(setIsPreloyder(true));

    try {
      let offsetTableRow = Number(isSelectPagination) * (Number(isCurrentPage) - 1);

      const officeUrl = selectedSiteUrl.trim() || undefined;
      const dataTable = await applicationPageRequests.getList(
        isSelectPagination,
        offsetTableRow,
        actualOfficeTable,
        undefined,
        officeUrl,
      );

      const dataTableFilter = await applicationPageRequests.getFilterList(actualOfficeTable);

      if (dataTable && dataTableFilter) {
        const normalizedTable = normalizeApplicationsResponse(dataTable);
        const roleAccess = resolveCountryAccessFromRole(userRole);
        const filteredData = filterRowsByAccess(normalizedTable.data, {
          officeUrlQuery: officeUrl,
          officeQuery: actualOfficeTable,
          roleHasFullCountryAccess: roleAccess.hasFullCountryAccess,
          roleAllowedOffices: roleAccess.allowedOffices,
        });
        console.log("Online Applications table data", filteredData);
        const tablePayload: ITableResponse = {
          data: filteredData,
          count_form: resolveAccessFilteredCount(
            filteredData.length,
            normalizedTable.data.length,
            normalizedTable.count_form,
          ),
          filters_list: dataTableFilter,
        };
        dispatch(setData(tablePayload));
        setTableFiltersList(dataTableFilter);
        setActualQuantityRows(tablePayload.count_form);
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
      console.error(error);
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  useEffect(() => {
    loadActualForm();
  }, [isCurrentPage, isSelectPagination, actualOfficeTable, selectedSiteUrl, userRole]);

  useEffect(() => {
    window.addEventListener("applyOnlineApplicationsChanged", syncApplyOnlineOpen);
    return () => {
      window.removeEventListener("applyOnlineApplicationsChanged", syncApplyOnlineOpen);
    };
  }, [syncApplyOnlineOpen]);

  useEffect(() => {
    setIsCurrentPage("1");
  }, [actualOfficeTable, actualFilterStatus, selectedSiteUrl]);

  const handleTaskCreated = useCallback(() => {
    navigate("/tasks");
  }, [navigate]);

  const refreshCurrentStudentUserInfo = useCallback(async (): Promise<IRowData["userInfo"] | undefined> => {
    if (currentStudentId == null) return undefined;
    try {
      const userInfo = await fetchApplicantUserInfo(currentStudentId);
      if (!userInfo) return undefined;

      const applicationRow = currentApplyInfoId != null ? applicationsTable.data.find((row) => row.id === currentApplyInfoId) : undefined;
      const mergedUserInfo = {
        ...userInfo,
        formStudy: applicationRow?.formStudy ?? userInfo.formStudy ?? "",
      };

      setCurrentDataPopUp(mergedUserInfo);
      return mergedUserInfo;
    } catch (error) {
      console.log("refreshCurrentStudentUserInfo error:", error);
      return undefined;
    }
  }, [currentStudentId, currentApplyInfoId, applicationsTable.data]);

  return (
    <>
      <div className={`applications-page-form ${isApplyOnlineOpen ? "show" : ""}`}>
        <ApplyOnline loadActualForm={loadActualForm} onCloseForm={closeApplyOnlineForm} />
      </div>

      <div className="container applications-page">
        <CustomFullNamePopUp
          isApPopUpFullName={isApPopUpFullName}
          setIsApPopUpFullName={setIsApPopUpFullName}
          setIsDocumentPreparationPopUpOpen={setIsDocumentPreparationPopUpOpen}
          currentDataPopUp={currentDataPopUp}
          currentStudentId={currentStudentId}
          currentApplyInfoId={currentApplyInfoId}
          tableContext="applications"
          onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
          onRefreshTableData={loadActualForm}
        />
        <CustomDocumentPreparationPopUp
          isOpen={isDocumentPreparationPopUpOpen}
          setIsOpen={setIsDocumentPreparationPopUpOpen}
          tableContext="applications"
          onSendSuccess={handleTaskCreated}
          onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
        />
        <APHeader
          isShowSettingsModal={isShowSettingsModal}
          setIsShowSettingsModal={setIsShowSettingsModal}
          setIsDocumentPreparationPopUpOpen={setIsDocumentPreparationPopUpOpen}
          loadActualForm={loadActualForm}
        />
        <APBody
          headerCheckboxRef={headerCheckboxRef}
          setIsApPopUpFullName={setIsApPopUpFullName}
          setCurrentDataPopUp={setCurrentDataPopUp}
          setCurrentStudentId={setCurrentStudentId}
          setCurrentApplyInfoId={setCurrentApplyInfoId}
          tableFiltersList={tableFiltersList}
          isShowSettingsModal={isShowSettingsModal}
        />
        <APFooter
          isCurrentPage={isCurrentPage}
          setIsCurrentPage={setIsCurrentPage}
          isSelectPagination={isSelectPagination}
          setIsSelectPagination={setIsSelectPagination}
          actualQuantityRows={actualQuantityRows}
        />
      </div>
    </>
  );
};

export default APLayout;
